import React, { useState, useEffect } from "react";
import {
    Card,
    Radio,
    Typography,
    Tag,
    Space,
    Image,
    Spin,
    message,
    Grid,
    List,
    Button,
    Modal,
    InputNumber,
    Popconfirm,
    Row,
    Col,
} from "antd";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    EditOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const getStoredToken = () => {
    const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
    for (const k of keys) {
        const v = localStorage.getItem(k);
        if (v) return v.replace(/^"|"$/g, "");
    }
    try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return user?.token || user?.accessToken || null;
    } catch {
        return null;
    }
};

const SupplierOrders = () => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadType, setLoadType] = useState("pending");

    // State for Update Timeline Modal
    const [updateModalVisible, setUpdateModalVisible] = useState(false);
    const [selectedPO, setSelectedPO] = useState(null);
    const [updateDays, setUpdateDays] = useState(1);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchOrders(loadType);
    }, [loadType]);

    const fetchOrders = async (type) => {
        setLoading(true);
        const token = getStoredToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const baseUrl = global.config?.APIURL?.BaseURL || "";

        try {
            if (type === "pending") {
                const res = await axios.get(
                    `${baseUrl}api/SupplierOrder/GetOrdersOfSupplierforList?IsCompleted=false`,
                    { headers }
                );
                const data = Array.isArray(res.data) ? res.data : [];
                const sortedData = [...data].sort((a, b) => {
                    const timeA = a.EstimatedDate ? new Date(a.EstimatedDate).getTime() : Number.MAX_SAFE_INTEGER;
                    const timeB = b.EstimatedDate ? new Date(b.EstimatedDate).getTime() : Number.MAX_SAFE_INTEGER;
                    if (timeA !== timeB) return timeA - timeB;
                    return b.ID - a.ID;
                });
                setOrders(sortedData);
            } else if (type === "all") {
                const [pendingRes, completedRes] = await Promise.all([
                    axios.get(
                        `${baseUrl}api/SupplierOrder/GetOrdersOfSupplierforList?IsCompleted=false`,
                        { headers }
                    ).catch(() => ({ data: [] })),
                    axios.get(
                        `${baseUrl}api/SupplierOrder/GetOrdersOfSupplierforList?IsCompleted=true`,
                        { headers }
                    ).catch(() => ({ data: [] })),
                ]);

                const pendingData = Array.isArray(pendingRes.data)
                    ? pendingRes.data
                    : [];
                const completedData = Array.isArray(completedRes.data)
                    ? completedRes.data
                    : [];

                const merged = [...pendingData, ...completedData];
                merged.sort((a, b) => {
                    const timeA = a.EstimatedDate ? new Date(a.EstimatedDate).getTime() : Number.MAX_SAFE_INTEGER;
                    const timeB = b.EstimatedDate ? new Date(b.EstimatedDate).getTime() : Number.MAX_SAFE_INTEGER;
                    if (timeA !== timeB) return timeA - timeB;
                    return b.ID - a.ID;
                });

                setOrders(merged);
            }
        } catch (err) {
            console.error("Error fetching supplier orders:", err);
            message.error("Failed to load supplier orders.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateDays = async () => {
        if (updateDays === null || updateDays === undefined) {
            message.error("Please enter a valid number of days");
            return;
        }
        setSubmitting(true);
        const token = getStoredToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const baseUrl = global.config?.APIURL?.BaseURL || "";

        try {
            await axios.post(
                `${baseUrl}api/SupplierOrder/UpdateDays?POID=${selectedPO.ID}&Days=${updateDays}`,
                {},
                { headers }
            );
            message.success("Timeline updated successfully!");
            setUpdateModalVisible(false);
            setUpdateDays(1);
            fetchOrders(loadType);
        } catch (err) {
            console.error(err);
            message.error("Failed to update timeline.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCompleteOrder = async (poid) => {
        const token = getStoredToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const baseUrl = global.config?.APIURL?.BaseURL || "";

        try {
            await axios.post(
                `${baseUrl}api/PurchaseOrder/MarkPOasComplete?POID=${poid}`,
                {},
                { headers }
            );
            message.success("Order marked as complete!");
            fetchOrders(loadType);
        } catch (err) {
            console.error(err);
            message.error("Failed to complete order.");
        }
    };

    return (
        <div
            style={{
                padding: isMobile ? "12px" : "24px",
                background: "#f5f6fa",
                minHeight: "100vh",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                    flexWrap: "wrap",
                    gap: "12px",
                }}
            >
                <Title level={isMobile ? 4 : 3} style={{ margin: 0, color: "#1890ff" }}>
                    Supplier Orders
                </Title>
                <Radio.Group
                    value={loadType}
                    onChange={(e) => setLoadType(e.target.value)}
                    buttonStyle="solid"
                    size={isMobile ? "middle" : "large"}
                >
                    <Radio.Button value="pending">Load Pending Only</Radio.Button>
                    <Radio.Button value="all">Load All</Radio.Button>
                </Radio.Group>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: "50px" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <List
                    grid={{
                        gutter: 16,
                        xs: 1,
                        sm: 1,
                        md: 2,
                        lg: 2,
                        xl: 3,
                        xxl: 4,
                    }}
                    dataSource={orders}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    renderItem={(record) => {
                        const isOverdue =
                            !record.IsCompleted &&
                            record.EstimatedDate &&
                            moment(record.EstimatedDate).startOf("day").diff(moment().startOf("day"), "days") < 0;

                        return (
                            <List.Item>
                                <Card
                                    hoverable
                                    style={{
                                        borderRadius: "12px",
                                        overflow: "hidden",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                                        backgroundColor: isOverdue ? "#fff1f0" : "#ffffff",
                                    }}
                                actions={[
                                    <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() => {
                                            setSelectedPO(record);
                                            let currentRemaining = 0;
                                            if (record.EstimatedDate) {
                                                currentRemaining = moment(record.EstimatedDate)
                                                    .startOf("day")
                                                    .diff(moment().startOf("day"), "days");
                                            }
                                            setUpdateDays(currentRemaining);
                                            setUpdateModalVisible(true);
                                        }}
                                        disabled={record.IsCompleted}
                                    >
                                        Update TimeLine
                                    </Button>,
                                    <Popconfirm
                                        title="Mark as Complete"
                                        description="Are you sure you want to mark this order as complete?"
                                        onConfirm={() => handleCompleteOrder(record.ID)}
                                        okText="Yes"
                                        cancelText="No"
                                        disabled={record.IsCompleted}
                                    >
                                        <Button
                                            type="link"
                                            icon={<CheckCircleOutlined />}
                                            disabled={record.IsCompleted}
                                        >
                                            Complete
                                        </Button>
                                    </Popconfirm>,
                                ]}
                            >
                                <div style={{ display: "flex", gap: "16px" }}>
                                    <div style={{ flexShrink: 0 }}>
                                        {record.ImageURL ? (
                                            <Image
                                                src={record.ImageURL}
                                                alt="Product"
                                                width={80}
                                                height={80}
                                                style={{ objectFit: "cover", borderRadius: "8px" }}
                                                fallback="https://via.placeholder.com/80?text=No+Image"
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    width: 80,
                                                    height: 80,
                                                    background: "#f0f0f0",
                                                    borderRadius: "8px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "12px",
                                                    color: "#999",
                                                }}
                                            >
                                                N/A
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            <Text strong style={{ fontSize: "16px" }} ellipsis>
                                                PO: #{record.ID}
                                            </Text>
                                            <Tag
                                                icon={
                                                    record.IsCompleted ? (
                                                        <CheckCircleOutlined />
                                                    ) : (
                                                        <ClockCircleOutlined />
                                                    )
                                                }
                                                color={record.IsCompleted ? "success" : "processing"}
                                                style={{ margin: 0 }}
                                            >
                                                {record.IsCompleted ? "Completed" : "Pending"}
                                            </Tag>
                                        </div>

                                        <Row gutter={[8, 4]} style={{ width: "100%" }}>
                                            <Col span={12}>
                                                <Text ellipsis>
                                                    <Text strong>Supplier:</Text> {record.SupplierName || "-"}{" "}
                                                    {record.SupplierDetail ? `(${record.SupplierDetail})` : ""}
                                                </Text>
                                            </Col>
                                            <Col span={12}>
                                                <Text ellipsis>
                                                    <Text strong>User:</Text> {record.User || "-"}
                                                </Text>
                                            </Col>
                                            <Col span={12}>
                                                <Text ellipsis>
                                                    <Text strong>Product:</Text> {record.Product || "-"}
                                                </Text>
                                            </Col>
                                            <Col span={12}>
                                                <Text ellipsis>
                                                    <Text strong>Detail:</Text> {record.Detail || "-"}
                                                </Text>
                                            </Col>
                                            <Col span={12}>
                                                <Text ellipsis>
                                                    <Text strong>Color:</Text> {record.Color || "-"}
                                                </Text>
                                            </Col>
                                            <Col span={12}>
                                                <Text ellipsis>
                                                    <Text strong>Days:</Text> {record.AproxDays ?? "-"}
                                                </Text>
                                            </Col>
                                            <Col span={12}>
                                                <Text ellipsis>
                                                    <Text strong>Qty:</Text> {record.Quantity || 0}{" "}
                                                    {record.Unit}
                                                </Text>
                                            </Col>
                                            <Col span={12}>
                                                <Text ellipsis>
                                                    <Text strong>Est Date:</Text>{" "}
                                                    {record.EstimatedDate
                                                        ? moment(record.EstimatedDate).format("DD MMM YYYY")
                                                        : "-"}
                                                </Text>
                                            </Col>
                                            <Col span={12}>
                                                {(() => {
                                                    if (!record.EstimatedDate) {
                                                        return (
                                                            <Text ellipsis>
                                                                <Text strong>Remaining:</Text> -
                                                            </Text>
                                                        );
                                                    }
                                                    const diff = moment(record.EstimatedDate)
                                                        .startOf("day")
                                                        .diff(moment().startOf("day"), "days");
                                                    if (diff < 0) {
                                                        return (
                                                            <Text ellipsis style={{ color: "#ff4d4f" }}>
                                                                <Text strong style={{ color: "#ff4d4f" }}>
                                                                    Overdue by:
                                                                </Text>{" "}
                                                                {Math.abs(diff)} Days
                                                            </Text>
                                                        );
                                                    }
                                                    return (
                                                        <Text ellipsis>
                                                            <Text strong>Remaining:</Text> {diff} Days
                                                        </Text>
                                                    );
                                                })()}
                                            </Col>
                                            {record.DaysHistory && (
                                                <Col span={12}>
                                                    <Text
                                                        type="secondary"
                                                        style={{ fontSize: "12px" }}
                                                        ellipsis
                                                    >
                                                        <Text strong>History:</Text> {record.DaysHistory}
                                                    </Text>
                                                </Col>
                                            )}

                                        </Row>
                                    </div>
                                </div>
                            </Card>
                        </List.Item>
                        );
                    }}
                />
            )}

            {/* Modal for Updating Timeline */}
            <Modal
                title={`Update Timeline - PO #${selectedPO?.ID}`}
                open={updateModalVisible}
                onOk={handleUpdateDays}
                onCancel={() => {
                    setUpdateModalVisible(false);
                    setUpdateDays(1);
                }}
                confirmLoading={submitting}
                okText="Update"
            >
                <div style={{ padding: "20px 0" }}>
                    <Space direction="vertical" style={{ width: "100%" }} size="large">
                        <Text>Please enter the number of days to update the timeline:</Text>
                        <div>
                            <Text strong style={{ marginRight: 8 }}>
                                Days:
                            </Text>
                            <InputNumber
                                value={updateDays}
                                onChange={setUpdateDays}
                                style={{ width: "100px" }}
                            />
                        </div>
                    </Space>
                </div>
            </Modal>
        </div>
    );
};

export default SupplierOrders;
